import type { CallbackInterface } from "recoil";

import { logAppError } from "Helpers/logger";
import { errorUpdater, loaderUpdater, pageState } from "State/page";
import type { PageStateType } from "Types/state";

import type { PanelEditState } from "Pages/GroupsList/model";
import { fetchResultsCallback } from "Pages/GroupsList/recoil/callbacks/callbacks";
import {
	panelEditState,
	resultsState,
} from "Pages/GroupsList/recoil/state/state";
import { notifyUpdateSuccess } from "Pages/GroupsList/services/notifications";
import {
	checkGroupConflict,
	fetchGroup,
	saveGroup,
} from "Pages/GroupsList/services/services";
import { gainsightTrack } from "mailup-react-helpers";

export const panelEditOpenCallback =
	({ reset, set, snapshot, ...cbi }: CallbackInterface) =>
	async (idGroup = 0) => {
		if (idGroup === 0) {
			set(panelEditState, {
				show: true,
				item: {
					Id: 0,
					IdList: 0,
					Name: "",
					Notes: "",
					Deleteable: false,
					Type: "Normal",
				},
				nameIsDirty: false,
				nameConflict: false,
			});
		} else {
			set<PageStateType>(pageState, loaderUpdater(true));

			try {
				const item = await fetchGroup(idGroup);

				if (item !== null) {
					set(panelEditState, {
						show: true,
						item,
						nameIsDirty: false,
						nameConflict: false,
					});
				} else {
					await fetchResultsCallback({ ...cbi, reset, set, snapshot })({}, {});
				}
			} catch (error) {
				console.error("### error", error);

				logAppError({
					Component: "GroupsList",
					Method: "panelEditOpenCallback",
					appError: error,
				});

				set<PageStateType>(pageState, errorUpdater);
			}

			set<PageStateType>(pageState, loaderUpdater(false));
		}
	};

export const panelEditCloseCallback =
	({ reset }: CallbackInterface) =>
	async () => {
		gainsightTrack("mup_recipients_click_hideRightPanel", {});

		reset(panelEditState);
	};

export const panelEditChangeNameCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (Name: string) => {
		const { item } = await snapshot.getPromise(panelEditState);

		if (item !== undefined) {
			set<PanelEditState>(panelEditState, (prev) => ({
				...prev,
				item: {
					...item,
					Name,
				},
				nameIsDirty: true,
				nameConflict: false,
			}));
		}
	};

export const panelEditChangeNotesCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (Notes: string) => {
		const { item } = await snapshot.getPromise(panelEditState);

		if (item !== undefined) {
			set<PanelEditState>(panelEditState, (prev) => ({
				...prev,
				item: {
					...item,
					Notes,
				},
			}));
		}
	};

export const panelEditChangeTypeCallback =
	({ set, snapshot }: CallbackInterface) =>
	async () => {
		const { item } = await snapshot.getPromise(panelEditState);

		if (item !== undefined) {
			set<PanelEditState>(panelEditState, (prev) => ({
				...prev,
				item: {
					...item,
					Type: item.Type === "Test" ? "Normal" : "Test",
				},
			}));
		}
	};

export const panelEditSaveCallback =
	({ set, snapshot, reset, ...cbi }: CallbackInterface) =>
	async () => {
		const { item } = await snapshot.getPromise(panelEditState);

		set<PageStateType>(pageState, loaderUpdater(true));

		if (item !== undefined) {
			try {
				const nameConflict = await checkGroupConflict(item);

				if (nameConflict) {
					set(panelEditState, (prev) => ({
						...prev,
						nameConflict,
					}));
				} else {
					const newItem = await saveGroup(item);

					set(panelEditState, (prev) => ({ ...prev, item: { ...newItem } }));

					if (item.Id > 0) {
						set(resultsState, (prev) =>
							prev.map((o) => (o.Id === item.Id ? newItem : o)),
						);
					} else {
						await fetchResultsCallback({ ...cbi, reset, set, snapshot })(
							{},
							{},
						);
					}

					reset(panelEditState);

					notifyUpdateSuccess();
				}
			} catch (error) {
				console.error("### error", error);

				logAppError({
					Component: "GroupsList",
					Method: "panelEditSaveCallback",
					appError: error,
				});

				set<PageStateType>(pageState, errorUpdater);
			}
		}

		set<PageStateType>(pageState, loaderUpdater(false));
	};
